import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormGetValues,
} from 'react-hook-form';
import { Grid, TextField, MenuItem } from '@mui/material';
import CuitInput from '../../../components/CuitInput';
import {
  TIPO_DOC_OPTIONS,
  SEXO_OPTIONS,
  ESTADO_CIVIL_OPTIONS,
  ESTADO_AFILIADO_OPTIONS,
} from '../../../types/afiliado';
import type { AfiliadoFormValues } from '../afiliadoSchema';
import { dniFromCuil } from '../../../utils/validation';

interface Props {
  control: Control<AfiliadoFormValues>;
  register: UseFormRegister<AfiliadoFormValues>;
  errors: FieldErrors<AfiliadoFormValues>;
  isEdit: boolean;
  setValue: UseFormSetValue<AfiliadoFormValues>;
  getValues: UseFormGetValues<AfiliadoFormValues>;
}

export default function TabDatosPersonales({
  control,
  register,
  errors,
  isEdit,
  setValue,
  getValues,
}: Props) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Controller
          name="cuil"
          control={control}
          render={({ field }) => (
            <CuitInput
              {...field}
              required
              fullWidth
              label="CUIL"
              error={!!errors.cuil}
              helperText={errors.cuil?.message}
              disabled={isEdit}
              onChange={(v) => {
                field.onChange(v);
                // Auto-completar el numero de documento desde el CUIL, solo si
                // esta vacio (no pisar un documento cargado a mano). Se usa
                // setValue de react-hook-form en vez de tocar el DOM: asi el
                // campo (controlado) refleja el valor y su label flota (shrink),
                // evitando que quede superpuesto al valor (ticket #487).
                const dni = dniFromCuil(v);
                if (dni && !getValues('numero_documento')) {
                  setValue('numero_documento', dni, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }
              }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          name="tipo_documento"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              select
              fullWidth
              required
              label="Tipo de documento"
              error={!!errors.tipo_documento}
              helperText={errors.tipo_documento?.message}
            >
              {TIPO_DOC_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          name="numero_documento"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              required
              fullWidth
              label="Numero de documento"
              error={!!errors.numero_documento}
              helperText={errors.numero_documento?.message}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          name="estado"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              select
              fullWidth
              required
              label="Estado del afiliado"
              error={!!errors.estado}
              helperText={errors.estado?.message}
            >
              {ESTADO_AFILIADO_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          {...register('apellido')}
          required
          fullWidth
          label="Apellido"
          error={!!errors.apellido}
          helperText={errors.apellido?.message}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          {...register('nombre')}
          required
          fullWidth
          label="Nombre"
          error={!!errors.nombre}
          helperText={errors.nombre?.message}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <Controller
          name="sexo"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              select
              fullWidth
              label="Sexo"
              error={!!errors.sexo}
              helperText={errors.sexo?.message}
            >
              <MenuItem value="">
                <em>Sin especificar</em>
              </MenuItem>
              {SEXO_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          {...register('fecha_nacimiento')}
          type="date"
          fullWidth
          label="Fecha de nacimiento"
          InputLabelProps={{ shrink: true }}
          error={!!errors.fecha_nacimiento}
          helperText={errors.fecha_nacimiento?.message}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <Controller
          name="estado_civil"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              select
              fullWidth
              label="Estado civil"
              error={!!errors.estado_civil}
              helperText={errors.estado_civil?.message}
            >
              <MenuItem value="">
                <em>Sin especificar</em>
              </MenuItem>
              {ESTADO_CIVIL_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          {...register('observaciones')}
          fullWidth
          multiline
          rows={2}
          label="Observaciones"
          error={!!errors.observaciones}
          helperText={errors.observaciones?.message}
        />
      </Grid>
    </Grid>
  );
}
